/**
 * E010-T017: StreamlainStreamObjects
 *
 * Stream objects renderer. Uses rich-text with StreamlainTextRenderer (200ms updates).
 * Renders reasoning/thinking content through the shared pill popover chrome.
 */

import {
  animateStreamLainDropdownPanelClose,
  decorateStreamLainDropdownPanel,
  getStreamLainDropdownMenuChromeCss,
  STREAMLAIN_DROPDOWN_MENU_PORTAL_Z_INDEX,
} from '@affine/core/components/dropdown-panel-chrome/chrome';
import { workspaceApiFetch } from '@affine/core/modules/cloud';
import type { FeatureFlagService } from '@affine/core/modules/feature-flag';
import type { PeekViewService } from '@affine/core/modules/peek-view';
import { I18n } from '@affine/i18n';
import { createLitPortal } from '@blocksuite/affine/components/portal';
import {
  Bound,
  getCommonBoundWithRotation,
} from '@blocksuite/affine/global/gfx';
import { WithDisposable } from '@blocksuite/affine/global/lit';
import type { ColorScheme } from '@blocksuite/affine/model';
import {
  type BlockStdScope,
  type EditorHost,
  ShadowlessElement,
} from '@blocksuite/affine/std';
import {
  type GfxController,
  GfxControllerIdentifier,
  type GfxModel,
} from '@blocksuite/affine/std/gfx';
import type { ExtensionType } from '@blocksuite/affine/store';
import type { NotificationService } from '@blocksuite/affine-shared/services';
import {
  renderStreamLainDisclosurePill,
  streamLainDisclosureStyles,
} from '@blocksuite/affine-shared/utils';
import { autoPlacement, shift } from '@floating-ui/dom';
import type { Signal } from '@preact/signals-core';
import { CANVAS_SELECTION_ROLE } from '@streamlain/canvas-selection-contract';
import { css, html, nothing, type PropertyValues, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import { extractAssistantSourceManifest } from '../../blocksuite/ai/_common/assistant-source-manifest';
import {
  assistantSourceManifestToDocPillItems,
  assistantSourceManifestToMailPillItems,
} from '../../blocksuite/ai/_common/assistant-source-pills';
import type { DocDisplayConfig } from '../../blocksuite/ai/components/ai-chat-chips';
import {
  getChatHistoryPortalBoundary,
  getChatHistoryPortalOverlayWidth,
  getChatHistoryPortalStyle,
} from '../../blocksuite/ai/components/ai-chat-chips/chat-input-portal-style';
import type { CanvasSelectionItem } from '../../blocksuite/ai/components/ai-chat-content/type';
import {
  streamLainDropdownMenuOffsetMiddleware,
  streamLainDropdownMenuSizeMiddleware,
} from '../../blocksuite/ai/components/ai-chat-input/dropdown-menu-chrome';
import type { StreamObject } from '../../blocksuite/ai/components/ai-chat-messages';
import {
  docSearchSourceStyles,
  type NormalizedAiDocSourcePillItem,
  normalizeDocSearchSourceResults,
  renderSourceList,
  toggleDocSearchCitationPortal,
} from '../../blocksuite/ai/components/ai-tools/doc-search-source';
import { getClientInstanceId } from '../../blocksuite/ai/provider/client-instance';
import { buildCanvasSelectionContextFromItems } from '../../blocksuite/ai/utils/canvas-selection-context-builder';
import { extractCanvasSelectionContext } from '../../blocksuite/ai/utils/extract';
import {
  localizedToolCallName,
  localizedToolName,
  localizedToolResultName,
  localizedToolText,
} from '../../blocksuite/ai/utils/stream-objects';
import type { AffineAIPanelState } from '../../blocksuite/ai/widgets/ai-panel/type';
import type { AgentAffectedRef } from './agent-activity-types';
import {
  executeAppOperatorClientAction,
  type ExecuteAppOperatorClientActionInput,
} from './app-operator/operator-bridge-registry';
import type { AppOperatorToolResultData } from './app-operator/operator-contract';

type ToolResultStreamObject = Extract<StreamObject, { type: 'tool-result' }>;

let thinkingPortalIdSeq = 0;

/** Resolve persisted tool names through the active UI locale. */
function getToolDisplayName(toolName: string, displayName?: string): string {
  return localizedToolName(toolName, displayName);
}

function getToolFailure(
  obj: StreamObject
): { success?: boolean; error?: string } | undefined {
  if (obj.type !== 'tool-result') return undefined;
  return obj.result as { success?: boolean; error?: string } | undefined;
}

function getAppOperatorToolResultData(
  obj: StreamObject
): AppOperatorToolResultData | undefined {
  if (obj.type !== 'tool-result' || obj.toolName !== 'app_execute_command') {
    return undefined;
  }
  const result = isRecord(obj.result) ? obj.result : {};
  if (result.success === false) return undefined;
  const data = isRecord(result.data) ? result.data : {};
  if (
    data.success !== true ||
    typeof data.customAppId !== 'string' ||
    typeof data.targetDocId !== 'string' ||
    typeof data.controlId !== 'string' ||
    typeof data.type !== 'string' ||
    typeof data.executionMode !== 'string'
  ) {
    return undefined;
  }
  if (
    data.executionMode !== 'browser_bridge' &&
    data.executionMode !== 'workspace_shell'
  ) {
    return undefined;
  }
  return data as unknown as AppOperatorToolResultData;
}

type ClientActionAckState = {
  status?: string;
  endpoint?: string;
  timeoutMs?: number;
  workspaceId?: string;
  targetDocId?: string;
  clientInstanceId?: string;
};

function getClientActionAckState(
  obj: StreamObject
): ClientActionAckState | undefined {
  if (obj.type !== 'tool-result') return undefined;
  const result = isRecord(obj.result) ? obj.result : {};
  const data = isRecord(result.data) ? result.data : {};
  const ack = isRecord(data.clientActionAck) ? data.clientActionAck : undefined;
  if (!ack) return undefined;
  return {
    status: stringValue(ack.status),
    endpoint: stringValue(ack.endpoint),
    timeoutMs:
      typeof ack.timeoutMs === 'number' && Number.isFinite(ack.timeoutMs)
        ? ack.timeoutMs
        : undefined,
    workspaceId: stringValue(ack.workspaceId),
    targetDocId: stringValue(ack.targetDocId),
    clientInstanceId: stringValue(ack.clientInstanceId),
  };
}

function getHostWorkspaceId(
  host: EditorHost | null | undefined
): string | undefined {
  const workspace = host?.store.workspace as { id?: string } | undefined;
  return typeof workspace?.id === 'string' ? workspace.id : undefined;
}

/**
 * Closed-loop gate for client-side canvas actions. A pending action may only
 * run in the tab the backend bound it to (clientInstanceId), inside the
 * matching workspace. Anything else ignores the action and sends no ack.
 */
function isClientActionBoundToThisClient(
  ack: ClientActionAckState | undefined,
  host: EditorHost | null | undefined
): boolean {
  if (!ack || ack.status !== 'pending') return false;
  if (!ack.clientInstanceId || ack.clientInstanceId !== getClientInstanceId()) {
    return false;
  }
  const hostWorkspaceId = getHostWorkspaceId(host);
  if (!ack.workspaceId || !hostWorkspaceId) return false;
  return ack.workspaceId === hostWorkspaceId;
}

function getToolResultCardState(
  obj: ToolResultStreamObject,
  displayName: string
) {
  const clientActionAck = getClientActionAckState(obj);
  if (clientActionAck?.status === 'pending') {
    return {
      name: localizedToolText(
        'com.streamlain.ai.tool.status.applying',
        `${displayName} applying`,
        { tool: displayName }
      ),
      status: 'running' as const,
      results: undefined,
    };
  }

  const result = getToolFailure(obj);
  const failed = result?.success === false;
  return {
    name: localizedToolResultName(
      obj.toolName,
      failed ? 'failed' : 'completed',
      displayName
    ),
    status: failed ? ('error' as const) : ('success' as const),
    results: failed && result?.error ? [{ title: result.error }] : undefined,
  };
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function nextFrame(): Promise<void> {
  return new Promise(resolve => requestAnimationFrame(() => resolve()));
}

async function waitForInsertedGfxElements(
  gfx: GfxController | null | undefined,
  beforeElementIds: ReadonlySet<string>
): Promise<GfxModel[]> {
  if (!gfx) return [];

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const insertedElements = gfx.gfxElements.filter(
      element => !beforeElementIds.has(element.id)
    );
    if (insertedElements.length > 0) {
      return insertedElements;
    }
    await nextFrame();
  }

  return gfx.gfxElements.filter(element => !beforeElementIds.has(element.id));
}

type ClientCanvasSelectionBounds = {
  x: number;
  y: number;
  w: number;
  h: number;
};

type ClientCanvasSelection = {
  docId: string;
  label: string;
  elementIds: string[];
  blockIds: string[];
  items: CanvasSelectionItem[];
  source: string;
  priority: string;
};

type InsertTemplateOnCanvasResult = {
  elementIds: string[];
  canvasSelection?: ClientCanvasSelection;
};

const TEMPLATE_SEMANTIC_SELECTION_ROLES = new Set<string>([
  CANVAS_SELECTION_ROLE.Area,
  CANVAS_SELECTION_ROLE.AreaLabel,
  CANVAS_SELECTION_ROLE.EditableTarget,
  CANVAS_SELECTION_ROLE.TemplateHelper,
]);

function toClientSelectionBounds(
  bound: unknown
): ClientCanvasSelectionBounds | null {
  if (!isRecord(bound)) return null;
  const x = bound.x;
  const y = bound.y;
  const w = bound.w ?? bound.width;
  const h = bound.h ?? bound.height;
  if (
    typeof x !== 'number' ||
    typeof y !== 'number' ||
    typeof w !== 'number' ||
    typeof h !== 'number' ||
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Number.isFinite(w) ||
    !Number.isFinite(h) ||
    w <= 0 ||
    h <= 0
  ) {
    return null;
  }
  return { x, y, w, h };
}

function richTextToString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  if (!value || typeof value !== 'object') return undefined;

  const record = value as Record<string, unknown>;
  const yText = record.yText;
  if (yText && yText !== value) {
    const text = richTextToString(yText);
    if (text) return text;
  }

  if (Array.isArray(record.delta)) {
    const text = record.delta
      .map(delta => (isRecord(delta) ? delta.insert : undefined))
      .filter((insert): insert is string => typeof insert === 'string')
      .join('')
      .trim();
    if (text) return text;
  }

  const toString = record.toString;
  if (typeof toString === 'function') {
    const text = String(toString.call(value)).trim();
    if (text && text !== '[object Object]') return text;
  }
  return undefined;
}

function getModelRecord(model: GfxModel): Record<string, unknown> {
  return model as unknown as Record<string, unknown>;
}

function getModelProps(model: GfxModel): Record<string, unknown> {
  const props = getModelRecord(model).props;
  return isRecord(props) ? props : {};
}

function getModelType(model: GfxModel): string {
  const record = getModelRecord(model);
  const props = getModelProps(model);
  return (
    stringValue(record.flavour) ??
    stringValue(record.type) ??
    stringValue(props.type) ??
    model.constructor?.name ??
    'canvas_element'
  );
}

function getModelText(model: GfxModel): string | undefined {
  const record = getModelRecord(model);
  const props = getModelProps(model);
  return (
    richTextToString(record.text) ??
    richTextToString(props.text) ??
    richTextToString(props.title) ??
    richTextToString(props.name) ??
    richTextToString(record.title) ??
    richTextToString(record.name)
  );
}

function getModelGroupId(model: GfxModel): string | undefined {
  const record = getModelRecord(model);
  const group = record.group;
  if (isRecord(group)) return stringValue(group.id);
  return stringValue(record.groupId) ?? stringValue(record.parentId);
}

function toClientSelectionItem(model: GfxModel): CanvasSelectionItem {
  const type = getModelType(model);
  const text = getModelText(model);
  const kind = type.startsWith('affine:') ? 'block' : 'canvas_element';
  const label = text ?? type;
  const bounds = toClientSelectionBounds(model.elementBound);
  const groupId = getModelGroupId(model);
  return {
    id: model.id,
    kind,
    type,
    label,
    ...(text ? { text } : {}),
    ...(bounds ? { bounds } : {}),
    ...(groupId ? { groupId } : {}),
  };
}

function buildClientCanvasSelection(
  docId: string,
  templateName: string | undefined,
  insertedElements: GfxModel[]
): ClientCanvasSelection | undefined {
  const selection = buildCanvasSelectionContextFromItems({
    docId,
    label: templateName?.trim() || 'Inserted canvas template',
    source: 'client-action',
    priority: 'high',
    templateName,
    items: insertedElements.map(toClientSelectionItem),
  });

  return selection ?? undefined;
}

function hasTemplateSemanticRoles(
  selection: ClientCanvasSelection | undefined
): boolean {
  if (!selection) return false;
  const visit = (item: CanvasSelectionItem): boolean =>
    Boolean(item.role && TEMPLATE_SEMANTIC_SELECTION_ROLES.has(item.role)) ||
    item.children?.some(visit) === true;
  return selection.items.some(visit);
}

function getInsertedCanvasSelection(
  host: EditorHost | null | undefined,
  docId: string,
  templateName: string | undefined,
  insertedElements: GfxModel[]
): ClientCanvasSelection | undefined {
  const semanticSelection = buildClientCanvasSelection(
    docId,
    templateName,
    insertedElements
  );
  if (hasTemplateSemanticRoles(semanticSelection)) {
    return semanticSelection;
  }

  const insertedElementIds = new Set(
    insertedElements.map(element => element.id)
  );
  const contextSelection =
    host?.store.id === docId ? extractCanvasSelectionContext(host)[0] : null;
  const contextElementIds = new Set([
    ...(contextSelection?.elementIds ?? []),
    ...(contextSelection?.blockIds ?? []),
  ]);
  const contextCoversInsertedElements =
    insertedElementIds.size > 0 &&
    [...insertedElementIds].every(elementId =>
      contextElementIds.has(elementId)
    );

  if (contextSelection && contextCoversInsertedElements) {
    return {
      ...contextSelection,
      label: templateName?.trim() || contextSelection.label,
      source: 'client-action',
      priority: 'high',
    };
  }

  return semanticSelection;
}

/**
 * sl-d0f0: Insert template via BlockSuite TemplateJob (frontend SSoT).
 * Called as side-effect when canvas_insert_template tool-result renders.
 */
async function insertTemplateOnCanvas(
  std: BlockStdScope,
  templateData: Record<string, unknown>,
  host: EditorHost | null | undefined
): Promise<InsertTemplateOnCanvasResult> {
  const { createTemplateJob } = await import('@blocksuite/affine/gfx/template');
  const { cloneDeep } = await import('lodash-es');

  const template = cloneDeep(templateData) as {
    name?: string;
    type?: string;
    content?: unknown;
    assets?: Record<string, string>;
  };

  const templateJob = createTemplateJob(std, 'template');
  const gfx = std.getOptional(GfxControllerIdentifier);
  const beforeElementIds = new Set(gfx?.gfxElements.map(element => element.id));

  // Load assets (images) if present
  if (template.assets) {
    await Promise.all(
      Object.entries(template.assets).map(([key, url]) =>
        fetch(url)
          .then(res => res.blob())
          .then(blob => templateJob.job.assets.set(key, blob))
      )
    );
  }

  const insertedBound = (await templateJob.insertTemplate(
    template.content
  )) as Bound | null;

  const insertedElements = await waitForInsertedGfxElements(
    gfx,
    beforeElementIds
  );
  const insertedElementIds = insertedElements.map(element => element.id);

  if (gfx && insertedBound) {
    gfx.viewport.setViewportByBound(insertedBound, [24, 24, 24, 24], true);
  }

  if (gfx && insertedElementIds.length > 0) {
    const bound =
      insertedBound ??
      getCommonBoundWithRotation(
        insertedElements.map(element => element.elementBound)
      );

    gfx.selection.set({
      elements: insertedElementIds,
      editing: false,
    });
    if (!insertedBound) {
      gfx.viewport.setViewportByBound(bound, [24, 24, 24, 24], true);
    }
  }

  if (!insertedBound && insertedElementIds.length === 0) {
    throw new Error('Template insertion produced no visible canvas elements');
  }

  return {
    elementIds: insertedElementIds,
    canvasSelection: getInsertedCanvasSelection(
      host,
      std.store.id,
      template.name,
      insertedElements
    ),
  };
}

type MindmapToolNode = {
  text: string;
  children?: MindmapToolNode[];
  xywh?: string;
};

type CanvasCreateMindmapData = {
  action?: string;
  docId?: string;
  tree?: MindmapToolNode;
  style?: 1 | 2 | 3 | 4;
  x?: number;
  y?: number;
};

function isGfxModel(model: unknown): model is GfxModel {
  return (
    !!model &&
    typeof model === 'object' &&
    'id' in model &&
    'elementBound' in model
  );
}

function normalizeMindmapToolNode(
  node: MindmapToolNode,
  xywh?: string
): MindmapToolNode {
  return {
    text: node.text,
    ...(xywh ? { xywh } : node.xywh ? { xywh: node.xywh } : {}),
    children: (node.children ?? []).map(child =>
      normalizeMindmapToolNode(child)
    ),
  };
}

function getCurrentDocId(host: EditorHost | null | undefined) {
  return host?.store.id;
}

function canvasResultSessionKey(toolCallId: string, action: string) {
  return `streamlain:canvas-result:${action}:${toolCallId}`;
}

function canvasFocusSessionKey(key: string) {
  return `streamlain:canvas-focus:${key}`;
}

function hasExecutedCanvasResult(toolCallId: string, action: string) {
  try {
    return sessionStorage.getItem(canvasResultSessionKey(toolCallId, action))
      ? true
      : false;
  } catch {
    return false;
  }
}

function beginCanvasResultExecution(toolCallId: string, action: string) {
  const key = canvasResultSessionKey(toolCallId, action);
  if (hasExecutedCanvasResult(toolCallId, action)) return false;
  try {
    sessionStorage.setItem(key, 'pending');
  } catch {
    // Session storage can be disabled; instance-level guards still apply.
  }
  return true;
}

function markExecutedCanvasResult(toolCallId: string, action: string) {
  try {
    sessionStorage.setItem(canvasResultSessionKey(toolCallId, action), '1');
  } catch {
    // Session storage can be disabled; in-memory guards still prevent rerender loops.
  }
}

function clearCanvasResultExecution(toolCallId: string, action: string) {
  try {
    sessionStorage.removeItem(canvasResultSessionKey(toolCallId, action));
  } catch {
    // Session storage can be disabled.
  }
}

export type WorkspaceDocSyncEngine = {
  addPriority?: (docId: string, priority: number) => void;
  flushAndWaitForSynced?: (docId: string) => Promise<void>;
};

function getCanvasActionDocId(
  std: BlockStdScope | null | undefined,
  host: EditorHost | null | undefined,
  fallbackDocId: string | undefined
): string | undefined {
  return host?.store.id ?? std?.store.id ?? fallbackDocId;
}

function getCanvasActionSyncEngine(
  std: BlockStdScope | null | undefined,
  host: EditorHost | null | undefined,
  workspaceDocSyncEngine: WorkspaceDocSyncEngine | null | undefined
): WorkspaceDocSyncEngine | undefined {
  if (workspaceDocSyncEngine) {
    return workspaceDocSyncEngine;
  }

  const workspace = host?.store.workspace ?? std?.workspace;
  return (
    workspace as { engine?: { doc?: WorkspaceDocSyncEngine } } | undefined
  )?.engine?.doc;
}

async function flushClientCanvasAction(
  std: BlockStdScope | null | undefined,
  host: EditorHost | null | undefined,
  fallbackDocId: string | undefined,
  workspaceDocSyncEngine: WorkspaceDocSyncEngine | null | undefined
): Promise<string> {
  const docId = getCanvasActionDocId(std, host, fallbackDocId);
  if (!docId) {
    throw new Error('Client-side canvas action has no document to sync');
  }

  const docEngine = getCanvasActionSyncEngine(
    std,
    host,
    workspaceDocSyncEngine
  );
  if (!docEngine?.flushAndWaitForSynced) {
    throw new Error('Workspace sync engine is not available');
  }

  docEngine.addPriority?.(docId, 100);
  await docEngine.flushAndWaitForSynced(docId);
  return docId;
}

async function acknowledgeClientAction(
  ack: ClientActionAckState | undefined,
  body: {
    success: boolean;
    clientInstanceId: string;
    error?: string;
    elementIds?: string[];
    data?: Record<string, unknown>;
  }
): Promise<void> {
  if (!ack?.endpoint || !ack.workspaceId) return;
  const response = await workspaceApiFetch(
    ack.endpoint,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    },
    {
      workspaceId: ack.workspaceId,
      category: 'foreground-action',
      mode: 'foreground',
      priority: 'foreground',
    }
  );
  if (!response.ok) {
    throw new Error(
      `Client action acknowledgement failed (${response.status})`
    );
  }
}

function hasFocusedCanvasResult(key: string) {
  try {
    return sessionStorage.getItem(canvasFocusSessionKey(key)) === '1';
  } catch {
    return false;
  }
}

function markFocusedCanvasResult(key: string) {
  try {
    sessionStorage.setItem(canvasFocusSessionKey(key), '1');
  } catch {
    // Session storage can be disabled; in-memory guard still prevents rerender loops.
  }
}

const CANVAS_RESULT_FOCUS_PADDING: [number, number, number, number] = [
  24, 24, 24, 24,
];
const CANVAS_RESULT_FOCUS_ZOOM_FACTOR = 0.85;
const CANVAS_RESULT_FOCUS_MAX_ZOOM = 1.1;

function focusCanvasIds(host: EditorHost | null | undefined, ids: string[]) {
  const gfx = host?.std.getOptional(GfxControllerIdentifier);
  if (!gfx || ids.length === 0) return false;

  const uniqueIds = [...new Set(ids)];
  const elements = uniqueIds
    .map(id => gfx.getElementById(id))
    .filter(isGfxModel);

  if (elements.length === 0) return false;

  const bound = getCommonBoundWithRotation(
    elements.map(element => element.elementBound)
  );
  gfx.selection.set({
    elements: elements.map(element => element.id),
    editing: false,
  });
  gfx.viewport.setViewportByBound(bound, CANVAS_RESULT_FOCUS_PADDING, false);
  gfx.viewport.setZoom(
    Math.min(
      gfx.viewport.zoom * CANVAS_RESULT_FOCUS_ZOOM_FACTOR,
      CANVAS_RESULT_FOCUS_MAX_ZOOM
    )
  );
  return true;
}

function focusCanvasElementIds(
  host: EditorHost | null | undefined,
  elementIds: string[]
) {
  return focusCanvasIds(host, elementIds);
}

async function createMindmapOnCanvas(
  host: EditorHost,
  data: CanvasCreateMindmapData
): Promise<string[]> {
  const { getSurfaceBlock } = await import('@blocksuite/affine/blocks/surface');
  const { fitContent } = await import('@blocksuite/affine/gfx/shape');
  const { MindmapStyle } = await import('@blocksuite/affine/model');
  const { MindmapElementModel, ShapeElementModel } =
    await import('@blocksuite/affine/model');

  const surface = getSurfaceBlock(host.store);
  const gfx = host.std.getOptional(GfxControllerIdentifier);
  if (!surface) {
    throw new Error('Surface block not found');
  }
  if (!data.tree?.text) {
    throw new Error('Mindmap tree is empty');
  }

  const viewportCenter = gfx?.viewport.viewportBounds.center ?? [0, 0];
  const rootX = data.x ?? viewportCenter[0];
  const rootY = data.y ?? viewportCenter[1];
  const tree = normalizeMindmapToolNode(data.tree, `[${rootX},${rootY},0,0]`);
  const style = data.style ?? MindmapStyle.FOUR;

  let mindmapId = '';
  host.store.transact(() => {
    mindmapId = surface.addElement({
      type: 'mindmap',
      children: tree,
      style,
    });
    const mindmap = surface.getElementById(mindmapId);
    if (mindmap instanceof MindmapElementModel) {
      for (const shape of mindmap.childElements) {
        if (shape instanceof ShapeElementModel) {
          fitContent(shape);
        }
      }
    }
  });

  await new Promise(resolve => setTimeout(resolve, 0));

  const mindmap = surface.getElementById(mindmapId);
  if (!(mindmap instanceof MindmapElementModel)) {
    throw new Error('Mindmap creation failed');
  }

  const elementIds = [
    mindmap.tree.element.id,
    ...mindmap.childElements.map(element => element.id),
  ];
  focusCanvasElementIds(host, elementIds);
  return elementIds;
}

type DocSearchStreamObject = StreamObject & {
  toolName?: string;
  args?: { query?: string };
  result?: unknown;
};

type MailSourceStreamObject = StreamObject & {
  toolName?: string;
  result?: unknown;
};

export function isDocSearchToolObject(
  streamObject: StreamObject
): streamObject is DocSearchStreamObject {
  return (
    (streamObject.type === 'tool-call' ||
      streamObject.type === 'tool-result') &&
    (streamObject.toolName === 'doc_semantic_search' ||
      streamObject.toolName === 'doc_keyword_search')
  );
}

function isMailSourceToolObject(
  streamObject: StreamObject
): streamObject is MailSourceStreamObject {
  return (
    streamObject.type === 'tool-result' &&
    [
      'mail_search',
      'mail_list_drafts',
      'mail_get_message',
      'mail_summarize',
      'mail_draft_reply',
      'mail_read_aloud_plan',
    ].includes(streamObject.toolName)
  );
}

function isPostAnswerSourceToolObject(streamObject: StreamObject): boolean {
  return (
    streamObject.type === 'tool-result' &&
    (isDocSearchToolObject(streamObject) ||
      isMailSourceToolObject(streamObject))
  );
}

type DocCreationToolResult = StreamObject & {
  type: 'tool-result';
  toolName: 'doc_create' | 'doc_compose';
  args: Record<string, unknown>;
  result?: unknown;
};

function isDocCreationToolResult(
  streamObject: StreamObject
): streamObject is DocCreationToolResult {
  return (
    streamObject.type === 'tool-result' &&
    (streamObject.toolName === 'doc_create' ||
      streamObject.toolName === 'doc_compose')
  );
}

function extractDocCreationResult(streamObject: StreamObject):
  | {
      docId: string;
      title: string;
    }
  | undefined {
  if (!isDocCreationToolResult(streamObject)) {
    return undefined;
  }

  const result = isRecord(streamObject.result) ? streamObject.result : {};
  const data = isRecord(result.data) ? result.data : {};
  const affectedRefs = (streamObject as { affectedRefs?: AgentAffectedRef[] })
    .affectedRefs;
  const docId =
    stringValue(data.docId) ??
    affectedRefs?.find(ref => ref.docId)?.docId ??
    undefined;
  if (!docId) {
    return undefined;
  }

  const title =
    stringValue(data.title) ??
    stringValue(streamObject.args.title) ??
    thisDocTitleFallback(streamObject.toolName);

  return { docId, title };
}

function thisDocTitleFallback(toolName: string | undefined): string {
  return toolName === 'doc_compose' ? 'Neues Dokument' : 'Dokument';
}

export function orderStreamObjectsForCopilotDisplay(answer: StreamObject[]) {
  const rest: StreamObject[] = [];
  const postAnswerSources: StreamObject[] = [];

  for (const item of answer) {
    if (isPostAnswerSourceToolObject(item)) {
      postAnswerSources.push(item);
    } else {
      rest.push(item);
    }
  }

  return {
    rest,
    postAnswerSources,
  };
}

function extractDocSearchSources(
  answer: StreamObject[],
  docDisplayService: DocDisplayConfig | undefined
): Array<{ result: NormalizedAiDocSourcePillItem; query: string }> {
  const manifest = extractAssistantSourceManifest(answer);
  const sourceItems = assistantSourceManifestToDocPillItems(
    {
      items: manifest.items.filter(item => item.kind === 'doc-search'),
    },
    docDisplayService
  );

  return normalizeDocSearchSourceResults(sourceItems).map(result => ({
    result,
    query: result.query ?? '',
  }));
}

function isCitationTrigger(value: EventTarget): value is HTMLElement {
  return (
    value instanceof HTMLElement &&
    value.classList.contains('doc-source-citation-inline') &&
    !!value.dataset.citationId
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function toRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

function readStringArray(value: unknown): string[] {
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  if (Array.isArray(value)) return value.flatMap(item => readStringArray(item));
  return [];
}

function readElementIdsFromToolData(data: Record<string, unknown>): string[] {
  return [
    ...readStringArray(data.elementId),
    ...readStringArray(data.elementIds),
    ...readStringArray(data.createdElementId),
    ...readStringArray(data.createdElementIds),
    ...readStringArray(data.shapeId),
    ...readStringArray(data.shapeIds),
    ...readStringArray(data.textId),
    ...readStringArray(data.textIds),
    ...readStringArray(data.connectorId),
    ...readStringArray(data.connectorIds),
    ...readStringArray(data.strokeIds),
    ...readStringArray(data.mindmapId),
    ...readStringArray(data.updatedElementIds),
  ];
}

function readBlockIdsFromToolData(data: Record<string, unknown>): string[] {
  return [
    ...readStringArray(data.blockId),
    ...readStringArray(data.blockIds),
    ...readStringArray(data.createdBlockId),
    ...readStringArray(data.createdBlockIds),
    ...readStringArray(data.databaseId),
    ...readStringArray(data.databaseIds),
    ...readStringArray(data.frameId),
    ...readStringArray(data.noteId),
  ];
}

function readAffectedRefs(streamObject: ToolResultStreamObject) {
  const result = isRecord(streamObject.result) ? streamObject.result : {};
  const affectedRefs = [
    ...(((streamObject as { affectedRefs?: AgentAffectedRef[] }).affectedRefs ??
      []) as AgentAffectedRef[]),
    ...(((result as { affectedRefs?: AgentAffectedRef[] }).affectedRefs ??
      []) as AgentAffectedRef[]),
  ];
  return affectedRefs.filter(ref => ref.docId);
}

export type CanvasFocusPlan = {
  key: string;
  elementIds: string[];
  blockIds: string[];
  targetDocId?: string;
  shouldOpenTargetDoc: boolean;
};

export function getCanvasFocusPlan(
  answer: StreamObject[],
  currentDocId: string | undefined
): CanvasFocusPlan | null {
  const entries: Array<{
    toolCallId: string;
    docId?: string;
    elementIds: string[];
    blockIds: string[];
  }> = [];

  for (const obj of answer) {
    if (obj.type !== 'tool-result') continue;
    const affectedRefs = readAffectedRefs(obj);
    const data = isRecord(obj.result)
      ? toRecord((obj.result as Record<string, unknown>).data)
      : {};
    const elementIds = [
      ...readElementIdsFromToolData(data),
      ...affectedRefs.flatMap(ref => (ref.elementId ? [ref.elementId] : [])),
    ];
    const blockIds = [
      ...readBlockIdsFromToolData(data),
      ...affectedRefs.flatMap(ref => (ref.blockId ? [ref.blockId] : [])),
    ];
    if (elementIds.length === 0 && blockIds.length === 0) continue;
    const docId =
      stringValue(data.docId) ??
      affectedRefs.find(ref => ref.docId)?.docId ??
      undefined;
    entries.push({
      toolCallId: obj.toolCallId,
      docId,
      elementIds,
      blockIds,
    });
  }

  if (entries.length === 0) return null;

  let targetDocId: string | undefined;
  for (const entry of entries) {
    if (entry.docId) {
      targetDocId = entry.docId;
    }
  }
  const targetEntries = targetDocId
    ? entries.filter(entry => entry.docId === targetDocId)
    : entries;
  const elementIds = [
    ...new Set(targetEntries.flatMap(entry => entry.elementIds)),
  ];
  const blockIds = [...new Set(targetEntries.flatMap(entry => entry.blockIds))];

  if (elementIds.length === 0 && blockIds.length === 0) return null;

  const key = [
    targetDocId ?? 'current',
    targetEntries.map(entry => entry.toolCallId).join('|'),
    elementIds.join('|'),
    blockIds.join('|'),
  ].join(':');

  return {
    key,
    elementIds,
    blockIds,
    targetDocId,
    shouldOpenTargetDoc:
      !!targetDocId && !!currentDocId && targetDocId !== currentDocId,
  };
}

export class StreamlainStreamObjects extends WithDisposable(ShadowlessElement) {
  static override styles = css`
    ${unsafeCSS(streamLainDisclosureStyles)}
    ${unsafeCSS(docSearchSourceStyles)}

    .thinking-block {
      display: inline-flex;
      max-width: 100%;
      margin: 4px 0 8px;
    }

    .thinking-trigger {
      max-width: 100%;
    }

    .thinking-label {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ai-reasoning-popover-content {
      color: var(--streamlain-text-secondary-color, #8e8e93);
      font-size: 13px;
      line-height: 1.5;
      opacity: 1;
    }

    .ai-reasoning-popover-content chat-content-rich-text,
    .ai-reasoning-popover-content chat-content-rich-text * {
      color: inherit;
      font-size: inherit;
      line-height: inherit;
    }

    .ai-reasoning-popover-content p,
    .ai-reasoning-popover-content ul,
    .ai-reasoning-popover-content ol {
      margin-block: 6px;
    }

    .ai-reasoning-popover-content h1,
    .ai-reasoning-popover-content h2,
    .ai-reasoning-popover-content h3,
    .ai-reasoning-popover-content h4,
    .ai-reasoning-popover-content h5,
    .ai-reasoning-popover-content h6 {
      margin: 0 0 8px;
      color: var(--streamlain-text-primary-color, #ffffff);
      font-size: 13px;
      line-height: 1.5;
      font-weight: 600;
    }

    .ai-reasoning-popover-content > :first-child,
    .ai-reasoning-popover-content chat-content-rich-text > :first-child {
      margin-top: 0;
    }

    .ai-reasoning-popover-content > :last-child,
    .ai-reasoning-popover-content chat-content-rich-text > :last-child {
      margin-bottom: 0;
    }

    @keyframes thinking-pulse {
      0%,
      100% {
        opacity: 0.5;
      }
      50% {
        opacity: 1;
      }
    }
    .thinking-label-active {
      animation: thinking-pulse 1.5s ease-in-out infinite;
    }

    .doc-source-citation-inline {
      display: inline;
      padding: 0;
      border: 0;
      border-radius: 4px;
      background: transparent;
      color: var(--streamlain-v2-button-primary);
      font: inherit;
      line-height: inherit;
      cursor: pointer;
    }

    .doc-source-citation-inline:hover {
      background: var(--streamlain-v2-layer-background-hoverOverlay);
    }
  `;

  @property({ attribute: false }) accessor answer!: StreamObject[];
  @property({ attribute: false }) accessor host: EditorHost | null | undefined;
  @property({ attribute: false }) accessor std:
    | BlockStdScope
    | null
    | undefined;
  @property({ attribute: false }) accessor state: AffineAIPanelState =
    'finished';
  @property({ attribute: false }) accessor width:
    | Signal<number | undefined>
    | undefined;
  @property({ attribute: false }) accessor extensions!: ExtensionType[];
  @property({ attribute: false })
  accessor affineFeatureFlagService!: FeatureFlagService;
  @property({ attribute: false }) accessor theme!: Signal<ColorScheme>;
  @property({ attribute: false }) accessor independentMode: boolean | undefined;
  @property({ attribute: false })
  accessor notificationService!: NotificationService;
  @property({ attribute: false }) accessor docDisplayService!: DocDisplayConfig;
  @property({ attribute: false }) accessor peekViewService!: PeekViewService;
  @property({ attribute: false }) accessor onOpenDoc!: (
    docId: string,
    sessionId?: string
  ) => void;
  @property({ attribute: false }) accessor workspaceDocSyncEngine:
    | WorkspaceDocSyncEngine
    | null
    | undefined;
  @property({ attribute: false }) accessor autoExecuteCanvasResults = false;

  // Track when reasoning started (for "thought for X seconds")
  private _reasoningStartTime: number | null = null;
  private _reasoningDuration: number | null = null;
  private _thinkingPortalAbortController: AbortController | null = null;
  private _thinkingPortalClosing = false;
  private _thinkingPortalPositionUpdate: (() => void) | null = null;
  private _thinkingPortalRenderUpdate: (() => void) | null = null;
  private _thinkingPortalRoot: HTMLElement | null = null;
  private _thinkingPortalTrigger: HTMLElement | null = null;
  private readonly _thinkingPortalId = `streamlain-thinking-popover-${++thinkingPortalIdSeq}`;
  private _thinkingPortalDirection: 'up' | 'down' = 'up';
  // sl-d0f0: Track template insertions to avoid double-execution on re-render
  private readonly _insertedTemplates = new Set<string>();
  private readonly _createdMindmaps = new Set<string>();
  private readonly _openedTargetDocs = new Set<string>();
  private readonly _canvasResultErrors = new Map<string, string>();
  private _focusedCanvasSelectionKey: string | null = null;

  private renderText(text: string) {
    return html`<chat-content-rich-text
      .text=${text}
      .state=${this.state}
      .extensions=${this.extensions}
      .affineFeatureFlagService=${this.affineFeatureFlagService}
      .theme=${this.theme}
    ></chat-content-rich-text>`;
  }

  private getCitationSources() {
    const lookup = new Map<
      string,
      { result: NormalizedAiDocSourcePillItem; query: string }
    >();

    for (const source of extractDocSearchSources(
      this.answer,
      this.docDisplayService
    )) {
      lookup.set(source.result.citationId, source);
    }

    return lookup;
  }

  private handleCitationClick(event: MouseEvent) {
    const trigger = event.composedPath().find(isCitationTrigger) as
      | HTMLElement
      | undefined;
    const citationId = trigger?.dataset.citationId;
    if (!citationId) {
      return;
    }

    const citation = this.getCitationSources().get(citationId);
    if (!citation || !trigger) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    toggleDocSearchCitationPortal(
      trigger,
      event,
      this,
      citation.result,
      citation.query
    );
  }

  private decorateCitationMarkers() {
    const citationIds = new Set(this.getCitationSources().keys());
    if (!citationIds.size) {
      return;
    }

    const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT, {
      acceptNode: node => {
        const parent = node.parentElement;
        if (!parent || !node.textContent?.includes('[')) {
          return NodeFilter.FILTER_REJECT;
        }
        if (
          parent.closest(
            [
              '.doc-source-citation-inline',
              '.streamlain-inline-disclosure',
              '.doc-source-portal',
              '.doc-source-citation-portal',
              'a',
              'button',
              'script',
              'style',
            ].join(',')
          )
        ) {
          return NodeFilter.FILTER_REJECT;
        }
        return /\[\d+\]/.test(node.textContent)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      },
    });

    const textNodes: Text[] = [];
    while (walker.nextNode()) {
      textNodes.push(walker.currentNode as Text);
    }

    for (const textNode of textNodes) {
      const text = textNode.textContent ?? '';
      const parts = text.split(/(\[(\d+)\])/g);
      if (parts.length <= 1) {
        continue;
      }

      const fragment = document.createDocumentFragment();
      let changed = false;
      for (let index = 0; index < parts.length; index += 1) {
        const part = parts[index];
        const markerMatch = /^\[(\d+)\]$/.exec(part);
        if (markerMatch && citationIds.has(markerMatch[1])) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'doc-source-citation-inline';
          button.dataset.citationId = markerMatch[1];
          button.textContent = part;
          fragment.append(button);
          changed = true;
          index += 1;
          continue;
        }
        if (part) {
          fragment.append(document.createTextNode(part));
        }
      }

      if (changed) {
        textNode.replaceWith(fragment);
      }
    }
  }

  private _isStreamingThinking(): boolean {
    if (this.state !== 'generating') return false;
    const last = this.answer.at(-1);
    return last?.type === 'reasoning';
  }

  private _getThinkingText() {
    const reasoning = this.answer.findLast(item => item.type === 'reasoning');
    return reasoning?.type === 'reasoning' ? reasoning.textDelta : '';
  }

  private _getThinkingPortalPanel(): HTMLElement | null {
    const portal = this._thinkingPortalRoot;
    if (!portal) return null;
    if (portal.matches('.ai-reasoning-popover')) return portal;
    return portal.querySelector<HTMLElement>('.ai-reasoning-popover');
  }

  private _getThinkingOverlayBoundary(): Element {
    return getChatHistoryPortalBoundary(this);
  }

  private _getThinkingPortalWidth(): number {
    return getChatHistoryPortalOverlayWidth(this);
  }

  private _getThinkingPortalStyle(): Record<string, string> {
    return getChatHistoryPortalStyle(this, this._getThinkingPortalWidth());
  }

  private _thinkingPortalMiddleware() {
    const boundary = this._getThinkingOverlayBoundary();
    return [
      streamLainDropdownMenuOffsetMiddleware(),
      autoPlacement({
        allowedPlacements: ['top-start', 'bottom-start'],
        boundary,
      }),
      streamLainDropdownMenuSizeMiddleware({ boundary }),
      shift({ boundary, crossAxis: true }),
    ];
  }

  private _decorateThinkingPortal(attempt = 0) {
    const panel = this._getThinkingPortalPanel();
    const trigger = this._thinkingPortalTrigger;
    if (!panel || !trigger) {
      if (attempt < 8) {
        requestAnimationFrame(() => this._decorateThinkingPortal(attempt + 1));
      }
      return;
    }

    decorateStreamLainDropdownPanel(panel, trigger);
    this._syncThinkingPortalDirection(panel, trigger);
  }

  private _syncThinkingPortalDirection(
    panel: HTMLElement,
    trigger: HTMLElement
  ) {
    const panelRect = panel.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const panelCenterY = panelRect.top + panelRect.height / 2;
    const triggerCenterY = triggerRect.top + triggerRect.height / 2;
    const direction = panelCenterY < triggerCenterY ? 'up' : 'down';
    if (this._thinkingPortalDirection !== direction) {
      this._thinkingPortalDirection = direction;
      this.requestUpdate();
    }
  }

  private _syncThinkingPortal() {
    this._thinkingPortalRenderUpdate?.();
    this._thinkingPortalPositionUpdate?.();
    requestAnimationFrame(() => this._decorateThinkingPortal());
  }

  private _closeThinkingPortal() {
    const abortController = this._thinkingPortalAbortController;
    if (!abortController) return;
    const panel = this._getThinkingPortalPanel();
    if (panel?.isConnected && !this._thinkingPortalClosing) {
      this._thinkingPortalClosing = true;
      animateStreamLainDropdownPanelClose(panel, () => {
        abortController.abort();
      });
      return;
    }
    abortController.abort();
  }

  private _openThinkingPortal(trigger: HTMLElement) {
    const abortController = new AbortController();
    this._thinkingPortalAbortController = abortController;
    this._thinkingPortalTrigger = trigger;
    this._thinkingPortalDirection = 'up';
    trigger.setAttribute('aria-expanded', 'true');

    const cleanup = () => {
      this._thinkingPortalAbortController = null;
      this._thinkingPortalClosing = false;
      this._thinkingPortalPositionUpdate = null;
      this._thinkingPortalRenderUpdate = null;
      this._thinkingPortalRoot = null;
      this._thinkingPortalTrigger = null;
      trigger.setAttribute('aria-expanded', 'false');
      this.requestUpdate();
    };
    abortController.signal.addEventListener('abort', cleanup, { once: true });

    setTimeout(() => {
      document.addEventListener(
        'click',
        event => {
          const target = event.target as Node | null;
          if (!target) return;
          const panel = this._getThinkingPortalPanel();
          if (trigger.contains(target)) return;
          if (panel?.contains(target)) return;
          this._closeThinkingPortal();
        },
        { signal: abortController.signal }
      );
    });
    document.addEventListener(
      'keydown',
      event => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        this._closeThinkingPortal();
      },
      { signal: abortController.signal }
    );

    const { portal, update } = createLitPortal({
      template: ({ updatePortal }) => {
        this._thinkingPortalRenderUpdate = updatePortal;
        return this._renderThinkingPortal();
      },
      portalStyles: {
        zIndex: STREAMLAIN_DROPDOWN_MENU_PORTAL_Z_INDEX,
      },
      container: document.body,
      shadowDom: false,
      positionStrategy: 'fixed',
      computePosition: {
        referenceElement: trigger,
        placement: 'top-start',
        middleware: this._thinkingPortalMiddleware(),
        autoUpdate: true,
      },
      abortController,
    });

    this._thinkingPortalRoot = portal;
    this._thinkingPortalPositionUpdate = update;
    this.requestUpdate();
    requestAnimationFrame(() => this._syncThinkingPortal());
  }

  private readonly _toggleThinkingPortal = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const trigger = event.currentTarget as HTMLElement;
    if (this._thinkingPortalAbortController) {
      this._closeThinkingPortal();
      return;
    }
    this._openThinkingPortal(trigger);
  };

  private _renderThinkingPortal() {
    const text = this._getThinkingText();

    return html`<style>
        ${getStreamLainDropdownMenuChromeCss()}
        ${streamLainDisclosureStyles}
      </style>
      <div
        id=${this._thinkingPortalId}
        class="ai-reasoning-popover streamlain-disclosure-popover-shell"
        style=${styleMap(this._getThinkingPortalStyle())}
      >
        <div
          class="streamlain-disclosure-popover-scroll streamlain-disclosure-popover-body ai-reasoning-popover-content"
        >
          ${text.trim()
            ? this.renderText(text)
            : html`<span>${I18n.t('com.streamlain.ai.thinking.empty')}</span>`}
        </div>
      </div>`;
  }

  private renderThinkingBlock(_text: string) {
    const isStreaming = this._isStreamingThinking();

    // Track timing
    if (isStreaming && this._reasoningStartTime === null) {
      this._reasoningStartTime = Date.now();
      this._reasoningDuration = null;
    }
    if (
      !isStreaming &&
      this._reasoningStartTime !== null &&
      this._reasoningDuration === null
    ) {
      this._reasoningDuration = Math.round(
        (Date.now() - this._reasoningStartTime) / 1000
      );
    }

    // Header label
    let label: string;
    if (isStreaming) {
      label = I18n.t('com.streamlain.ai.thinking.streaming');
    } else if (
      this._reasoningDuration !== null &&
      this._reasoningDuration > 0
    ) {
      label = I18n.t('com.streamlain.ai.thinking.duration', {
        seconds: this._reasoningDuration,
      });
    } else {
      label = I18n.t('com.streamlain.ai.thinking.label');
    }

    const expanded = Boolean(this._thinkingPortalAbortController);
    const shellClass = [
      'thinking-block',
      'streamlain-disclosure-overlay-anchor',
      this._thinkingPortalDirection === 'down'
        ? 'streamlain-disclosure-overlay-down'
        : 'streamlain-disclosure-overlay-up',
    ].join(' ');

    return html`
      <span class=${shellClass}>
        ${renderStreamLainDisclosurePill({
          ariaHasPopup: 'dialog',
          className: 'thinking-trigger',
          controlsId: this._thinkingPortalId,
          expanded,
          expandable: true,
          label: html`
            <span
              class="thinking-label ${isStreaming
                ? 'thinking-label-active'
                : ''}"
              title=${label}
              >${label}</span
            >
          `,
          title: label,
          onClick: this._toggleThinkingPortal,
        })}
      </span>
    `;
  }

  private renderToolCall(obj: StreamObject) {
    if (obj.type !== 'tool-call') return nothing;
    const w = this.width;
    switch (obj.toolName) {
      case 'web_crawl':
        return html`<web-crawl-tool .data=${obj} .width=${w}></web-crawl-tool>`;
      case 'web_search':
        return html`<web-search-tool
          .data=${obj}
          .width=${w}
        ></web-search-tool>`;
      case 'doc_compose':
        return html`<doc-compose-tool
          .std=${this.std || this.host?.std}
          .data=${obj}
          .width=${w}
          .theme=${this.theme}
          .notificationService=${this.notificationService}
        ></doc-compose-tool>`;
      case 'code_artifact':
        return html`<code-artifact-tool
          .std=${this.std || this.host?.std}
          .data=${obj}
          .width=${w}
          .theme=${this.theme}
        ></code-artifact-tool>`;
      case 'doc_semantic_search':
        return html`<doc-semantic-search-result
          .data=${obj}
          .width=${w}
          .peekViewService=${this.peekViewService}
        ></doc-semantic-search-result>`;
      case 'doc_keyword_search':
        return html`<doc-keyword-search-result
          .data=${obj}
          .width=${w}
        ></doc-keyword-search-result>`;
      case 'doc_read':
        return html`<doc-read-result
          .data=${obj}
          .width=${w}
        ></doc-read-result>`;
      case 'section_edit':
        return html`<section-edit-tool
          .data=${obj}
          .extensions=${this.extensions}
          .affineFeatureFlagService=${this.affineFeatureFlagService}
          .notificationService=${this.notificationService}
          .theme=${this.theme}
          .host=${this.host}
          .independentMode=${this.independentMode}
        ></section-edit-tool>`;
      default: {
        const dn = localizedToolCallName(
          obj.toolName ?? '',
          (obj as Record<string, unknown>).displayName as string | undefined
        );
        return html`<tool-call-card .name=${dn} .width=${w}></tool-call-card>`;
      }
    }
  }

  private renderToolResult(obj: StreamObject) {
    if (obj.type !== 'tool-result') return nothing;
    const w = this.width;
    const createdDoc = extractDocCreationResult(obj);
    if (createdDoc) {
      return html`<tool-result-card
        .name=${localizedToolText(
          'com.streamlain.ai.tool.doc_created',
          'Document created'
        )}
        .results=${this.affectedRefResults(obj)}
        .width=${w}
      ></tool-result-card>`;
    }

    const appOperatorData = getAppOperatorToolResultData(obj);
    if (appOperatorData) {
      const clientActionAck = getClientActionAckState(obj);
      const boundToThisClient = isClientActionBoundToThisClient(
        clientActionAck,
        this.host
      );

      if (
        this.autoExecuteCanvasResults &&
        boundToThisClient &&
        !this._canvasResultErrors.has(obj.toolCallId) &&
        beginCanvasResultExecution(obj.toolCallId, 'app-operator')
      ) {
        const executeInput: ExecuteAppOperatorClientActionInput = {
          data: appOperatorData,
          clientInstanceId: getClientInstanceId(),
          acknowledge: body => acknowledgeClientAction(clientActionAck, body),
        };
        executeAppOperatorClientAction(executeInput)
          .then(() => {
            markExecutedCanvasResult(obj.toolCallId, 'app-operator');
            this.requestUpdate();
          })
          .catch(error => {
            const message = getErrorMessage(error);
            this._canvasResultErrors.set(obj.toolCallId, message);
            clearCanvasResultExecution(obj.toolCallId, 'app-operator');
            console.error('[app_execute_command] Failed:', error);
            this.requestUpdate();
          });
      }

      const dn = getToolDisplayName(obj.toolName, obj.displayName);
      const localError = this._canvasResultErrors.get(obj.toolCallId);
      const card = localError
        ? {
            name: localizedToolResultName(obj.toolName, 'failed', dn),
            status: 'error' as const,
            results: [{ title: localError }],
          }
        : getToolResultCardState(obj, dn);
      return html`<tool-result-card
        .name=${card.name}
        .status=${card.status}
        .results=${card.results ?? this.affectedRefResults(obj)}
        .width=${w}
      ></tool-result-card>`;
    }

    // sl-d0f0: Handle canvas_insert_template — trigger frontend-side TemplateJob insertion
    if (
      obj.toolName === 'canvas_insert_template' &&
      obj.type === 'tool-result'
    ) {
      const result = obj.result as
        | {
            data?: {
              action?: string;
              docId?: string;
              templateName?: string;
              templateData?: Record<string, unknown>;
            };
          }
        | undefined;

      if (
        result?.data?.action === 'insert_template' &&
        result.data.templateData
      ) {
        const templateData = result.data.templateData;
        const templateKey = `${result.data.templateName}-${obj.toolCallId}`;
        const clientActionAck = getClientActionAckState(obj);
        const targetDocId = clientActionAck?.targetDocId ?? result.data.docId;
        const currentDocId = getCurrentDocId(this.host);
        // Closed loop: only the bound tab executes, only into the bound doc.
        // History renders (no pending ack) never re-execute.
        const boundToThisClient = isClientActionBoundToThisClient(
          clientActionAck,
          this.host
        );

        if (this.autoExecuteCanvasResults && boundToThisClient && targetDocId) {
          // Never navigate away for template side effects: execute only when
          // the bound target doc is already the active editor doc. Otherwise
          // ignore without ack — the backend wait times out and the tool
          // result becomes a clean failure instead of a wrong-doc mutation.
          const std =
            this.std?.store.id === targetDocId
              ? this.std
              : currentDocId === targetDocId
                ? this.host?.std
                : undefined;

          if (
            std &&
            !this._canvasResultErrors.has(obj.toolCallId) &&
            !this._insertedTemplates.has(templateKey) &&
            beginCanvasResultExecution(obj.toolCallId, 'template')
          ) {
            this._insertedTemplates.add(templateKey);
            this._canvasResultErrors.delete(obj.toolCallId);
            insertTemplateOnCanvas(std, templateData, this.host)
              .then(async ({ elementIds, canvasSelection }) => {
                try {
                  const docId = await flushClientCanvasAction(
                    std,
                    this.host,
                    targetDocId,
                    this.workspaceDocSyncEngine
                  );
                  await acknowledgeClientAction(clientActionAck, {
                    success: true,
                    clientInstanceId: getClientInstanceId(),
                    elementIds,
                    data: {
                      docId,
                      elementIds,
                      ...(canvasSelection ? { canvasSelection } : {}),
                    },
                  });
                } catch (err) {
                  const message = getErrorMessage(err);
                  this._canvasResultErrors.set(obj.toolCallId, message);
                  try {
                    await acknowledgeClientAction(clientActionAck, {
                      success: false,
                      clientInstanceId: getClientInstanceId(),
                      error: message,
                      elementIds,
                      data: { elementIds },
                    });
                  } catch {
                    // Backend wait will time out if the acknowledgement cannot be delivered.
                  }
                  console.error(
                    '[canvas_insert_template] Sync or ack failed:',
                    err
                  );
                }
                markExecutedCanvasResult(obj.toolCallId, 'template');
                this.requestUpdate();
              })
              .catch(async err => {
                const message = getErrorMessage(err);
                this._insertedTemplates.delete(templateKey);
                this._canvasResultErrors.set(obj.toolCallId, message);
                clearCanvasResultExecution(obj.toolCallId, 'template');
                try {
                  await acknowledgeClientAction(clientActionAck, {
                    success: false,
                    clientInstanceId: getClientInstanceId(),
                    error: message,
                  });
                } catch {
                  // Backend wait will time out if the acknowledgement cannot be delivered.
                }
                console.error('[canvas_insert_template] Failed:', err);
                this.requestUpdate();
              });
          }
        }
      }

      const dn = getToolDisplayName(obj.toolName, obj.displayName);
      const localError = this._canvasResultErrors.get(obj.toolCallId);
      const card = localError
        ? {
            name: localizedToolResultName(obj.toolName, 'failed', dn),
            status: 'error' as const,
            results: [{ title: localError }],
          }
        : getToolResultCardState(obj, dn);
      return html`<tool-result-card
        .name=${card.name}
        .status=${card.status}
        .results=${card.results ?? this.affectedRefResults(obj)}
        .width=${w}
      ></tool-result-card>`;
    }

    if (obj.toolName === 'canvas_create_mindmap') {
      const result = obj.result as
        | {
            data?: CanvasCreateMindmapData;
          }
        | undefined;
      const data = result?.data;
      if (data?.action === 'create_mindmap') {
        const clientActionAck = getClientActionAckState(obj);
        const targetDocId = clientActionAck?.targetDocId ?? data.docId;
        const currentDocId = getCurrentDocId(this.host);
        const mindmapKey = `${targetDocId ?? 'current'}-${obj.toolCallId}`;
        // Closed loop: only the bound tab executes, only into the bound doc.
        // History renders (no pending ack) never re-execute.
        const boundToThisClient = isClientActionBoundToThisClient(
          clientActionAck,
          this.host
        );

        if (this.autoExecuteCanvasResults && boundToThisClient && targetDocId) {
          if (currentDocId !== targetDocId) {
            this.openTargetDocOnce(targetDocId, obj.toolCallId);
          } else if (
            this.host &&
            !this._createdMindmaps.has(mindmapKey) &&
            beginCanvasResultExecution(obj.toolCallId, 'mindmap')
          ) {
            this._createdMindmaps.add(mindmapKey);
            createMindmapOnCanvas(this.host, data)
              .then(async elementIds => {
                try {
                  const docId = await flushClientCanvasAction(
                    this.host?.std,
                    this.host,
                    targetDocId,
                    this.workspaceDocSyncEngine
                  );
                  await acknowledgeClientAction(clientActionAck, {
                    success: true,
                    clientInstanceId: getClientInstanceId(),
                    elementIds,
                    data: { docId, elementIds },
                  });
                } catch (err) {
                  const message = getErrorMessage(err);
                  try {
                    await acknowledgeClientAction(clientActionAck, {
                      success: false,
                      clientInstanceId: getClientInstanceId(),
                      error: message,
                      elementIds,
                      data: { elementIds },
                    });
                  } catch {
                    // Backend wait will time out if the acknowledgement cannot be delivered.
                  }
                  console.error(
                    '[canvas_create_mindmap] Sync or ack failed:',
                    err
                  );
                }
                markExecutedCanvasResult(obj.toolCallId, 'mindmap');
                this.requestUpdate();
              })
              .catch(async err => {
                const message = getErrorMessage(err);
                this._createdMindmaps.delete(mindmapKey);
                clearCanvasResultExecution(obj.toolCallId, 'mindmap');
                try {
                  await acknowledgeClientAction(clientActionAck, {
                    success: false,
                    clientInstanceId: getClientInstanceId(),
                    error: message,
                  });
                } catch {
                  // Backend wait will time out if the acknowledgement cannot be delivered.
                }
                console.error('[canvas_create_mindmap] Failed:', err);
                this.requestUpdate();
              });
          }
        }
      }

      const dn = getToolDisplayName(obj.toolName, obj.displayName);
      const card = getToolResultCardState(obj, dn);
      return html`<tool-result-card
        .name=${card.name}
        .status=${card.status}
        .results=${card.results ?? this.affectedRefResults(obj)}
        .width=${w}
      ></tool-result-card>`;
    }

    switch (obj.toolName) {
      case 'web_crawl':
        return html`<web-crawl-tool .data=${obj} .width=${w}></web-crawl-tool>`;
      case 'web_search':
        return html`<web-search-tool
          .data=${obj}
          .width=${w}
        ></web-search-tool>`;
      case 'doc_compose':
        return html`<doc-compose-tool
          .std=${this.std || this.host?.std}
          .data=${obj}
          .width=${w}
          .theme=${this.theme}
          .notificationService=${this.notificationService}
        ></doc-compose-tool>`;
      case 'code_artifact':
        return html`<code-artifact-tool
          .std=${this.std || this.host?.std}
          .data=${obj}
          .width=${w}
          .theme=${this.theme}
          .notificationService=${this.notificationService}
        ></code-artifact-tool>`;
      case 'doc_semantic_search':
        if (this.state !== 'finished') {
          return this.renderDeferredSourceResult(obj);
        }
        return html`<doc-semantic-search-result
          .data=${obj}
          .width=${w}
          .docDisplayService=${this.docDisplayService}
          .peekViewService=${this.peekViewService}
          .onOpenDoc=${this.onOpenDoc}
        ></doc-semantic-search-result>`;
      case 'doc_keyword_search':
        if (this.state !== 'finished') {
          return this.renderDeferredSourceResult(obj);
        }
        return html`<doc-keyword-search-result
          .data=${obj}
          .width=${w}
          .peekViewService=${this.peekViewService}
          .onOpenDoc=${this.onOpenDoc}
        ></doc-keyword-search-result>`;
      case 'mail_search':
      case 'mail_get_message':
      case 'mail_summarize':
      case 'mail_draft_reply':
      case 'mail_read_aloud_plan':
        if (this.state !== 'finished') {
          return this.renderDeferredSourceResult(obj);
        }
        return this.renderMailSourceResult(obj);
      case 'doc_read':
        return html`<doc-read-result
          .data=${obj}
          .width=${w}
          .peekViewService=${this.peekViewService}
          .onOpenDoc=${this.onOpenDoc}
        ></doc-read-result>`;
      case 'section_edit':
        return html`<section-edit-tool
          .data=${obj}
          .extensions=${this.extensions}
          .affineFeatureFlagService=${this.affineFeatureFlagService}
          .notificationService=${this.notificationService}
          .theme=${this.theme}
          .host=${this.host}
          .independentMode=${this.independentMode}
        ></section-edit-tool>`;
      case 'image_generate': {
        const imgResult = obj.result as
          | {
              success?: boolean;
              error?: string;
              data?: { blobId?: string; revisedPrompt?: string };
            }
          | undefined;
        const blobId = imgResult?.data?.blobId;
        if (blobId) {
          // Live streaming: resolve blob ID to copilot bucket URL (same as user-paste)
          const imgUrl = `/api/workspaces/copilot/blobs/${encodeURIComponent(blobId)}`;
          return html`<chat-content-images
            .images=${[imgUrl]}
          ></chat-content-images>`;
        }
        // sl-8m4o.1: History reload — image rendered via renderImages() from attachments.
        // Only show failure card when tool actually failed, not when data is just absent.
        if (imgResult?.success === false) {
          return html`<tool-result-card
            .name=${localizedToolResultName('image_generate', 'failed')}
            .status=${'error'}
            .results=${imgResult.error ? [{ title: imgResult.error }] : []}
            .width=${w}
          ></tool-result-card>`;
        }
        // Success without data = history reload, image comes from attachments (DRY)
        return nothing;
      }
      // sl-vt9e.3.1: KG source buttons — clickable doc references
      case 'kg_source': {
        if (this.state !== 'finished') {
          return this.renderDeferredSourceResult(obj);
        }
        const kgResult = obj.result as
          | {
              success?: boolean;
              data?: {
                sources?: {
                  docId: string;
                  docTitle: string;
                  blockIds?: string[];
                }[];
              };
            }
          | undefined;
        const sources = kgResult?.data?.sources ?? [];
        if (!sources.length) return nothing;
        return html`<div style="padding:8px 0 4px">
          <div
            style="font-size:11px;color:var(--streamlain-text-secondary-color);margin-bottom:6px"
          >
            ${localizedToolText(
              'com.streamlain.ai.tool.kg_source.sources',
              'Sources'
            )}
          </div>
          <div style="display:flex;flex-wrap:wrap;gap:6px;align-items:center">
            ${sources.map(
              src =>
                html`<button
                  style="
                  display:inline-flex;align-items:center;gap:4px;
                  padding:3px 10px;border-radius:12px;
                  border:1px solid var(--streamlain-border-color);
                  background:var(--streamlain-background-overlay-panel-color);
                  color:var(--streamlain-text-primary-color);
                  font-size:12px;cursor:pointer;
                  transition:all 0.15s;
                "
                  @mouseenter=${(e: MouseEvent) => {
                    (e.target as HTMLElement).style.background =
                      'var(--streamlain-hover-color)';
                  }}
                  @mouseleave=${(e: MouseEvent) => {
                    (e.target as HTMLElement).style.background =
                      'var(--streamlain-background-overlay-panel-color)';
                  }}
                  @click=${() => {
                    this.dispatchEvent(
                      new CustomEvent('navigate-to-source', {
                        bubbles: true,
                        composed: true,
                        detail: {
                          kind: 'canvas-affected-ref',
                          docId: src.docId,
                          blockIds: src.blockIds ?? [],
                          requestId: `source-${obj.toolCallId}-${src.docId}`,
                        },
                      })
                    );
                  }}
                >
                  ${src.docTitle}
                </button>`
            )}
          </div>
        </div>`;
      }
      // sl-vt9e.3.2: Memory recall — info card
      // Live: count from result.data. History: displayName carries the count.
      case 'memory_recall': {
        const mrResult = obj.result as
          | { data?: { count?: number } }
          | undefined;
        const count = mrResult?.data?.count;
        const label =
          count !== undefined
            ? localizedToolText(
                'com.streamlain.ai.tool.memory_recall.loaded',
                `Memories loaded (${count})`,
                { count }
              )
            : localizedToolText(
                'com.streamlain.ai.tool.memory_recall.loaded-unknown',
                ((obj as Record<string, unknown>).displayName as
                  | string
                  | undefined) ?? 'Memories loaded'
              );
        return html`<tool-result-card
          .name=${label}
          .results=${this.affectedRefResults(obj)}
          .width=${w}
        ></tool-result-card>`;
      }
      // sl-vt9e.3.2: Memory store + KG extraction — use displayName from backend
      case 'memory_store':
      case 'kg_extraction': {
        const dn = getToolDisplayName(
          obj.toolName ?? '',
          (obj as Record<string, unknown>).displayName as string | undefined
        );
        return html`<tool-result-card
          .name=${dn}
          .results=${this.affectedRefResults(obj)}
          .width=${w}
        ></tool-result-card>`;
      }
      default: {
        const dn = getToolDisplayName(
          obj.toolName ?? '',
          (obj as Record<string, unknown>).displayName as string | undefined
        );
        const card = getToolResultCardState(obj, dn);
        return html`<tool-result-card
          .name=${card.name}
          .status=${card.status}
          .results=${card.results ?? this.affectedRefResults(obj)}
          .width=${w}
        ></tool-result-card>`;
      }
    }
  }

  private renderDeferredSourceResult(obj: StreamObject) {
    if (obj.type !== 'tool-result') return nothing;
    const displayName = getToolDisplayName(
      obj.toolName ?? '',
      (obj as Record<string, unknown>).displayName as string | undefined
    );
    return html`<tool-result-card
      .name=${localizedToolResultName(obj.toolName, 'completed', displayName)}
      .status=${'success'}
      .results=${this.affectedRefResults(obj)}
      .width=${this.width}
    ></tool-result-card>`;
  }

  private renderMailSourceResult(obj: StreamObject) {
    if (obj.type !== 'tool-result') return nothing;
    const sourceResults = assistantSourceManifestToMailPillItems(
      extractAssistantSourceManifest([obj])
    );
    if (!sourceResults.length) return this.renderDeferredSourceResult(obj);
    return renderSourceList(this, sourceResults, 'Mail');
  }

  protected override render() {
    const { rest, postAnswerSources } = orderStreamObjectsForCopilotDisplay(
      this.answer
    );
    return html`<div @click=${this.handleCitationClick}>
      ${rest.map(d => {
        switch (d.type) {
          case 'text-delta':
            return this.renderText(d.textDelta);
          case 'reasoning':
            return this.renderThinkingBlock(d.textDelta);
          case 'tool-call':
            return this.renderToolCall(d);
          case 'tool-result':
            return this.renderToolResult(d);
          default:
            return nothing;
        }
      })}
      ${this.state === 'finished'
        ? postAnswerSources.map(d => this.renderToolResult(d))
        : nothing}
    </div>`;
  }

  override disconnectedCallback() {
    this._thinkingPortalAbortController?.abort();
    super.disconnectedCallback();
  }

  override updated(changedProperties: PropertyValues<this>) {
    super.updated(changedProperties);
    if (!changedProperties.has('answer') && !changedProperties.has('state')) {
      return;
    }
    if (this._thinkingPortalAbortController) {
      this._syncThinkingPortal();
    }
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.decorateCitationMarkers();
      this.focusCanvasResultsFromLatestAnswer();
      window.setTimeout(() => {
        if (!this.isConnected) return;
        this.decorateCitationMarkers();
        this.focusCanvasResultsFromLatestAnswer();
      }, 80);
    });
  }

  private openTargetDocOnce(docId: string, toolCallId: string) {
    const key = `${docId}:${toolCallId}`;
    if (this._openedTargetDocs.has(key)) return;
    this._openedTargetDocs.add(key);
    this.onOpenDoc?.(docId);
    window.setTimeout(() => {
      if (!this.isConnected) return;
      this.requestUpdate();
    }, 250);
  }

  private focusCanvasResultsFromLatestAnswer() {
    if (!this.autoExecuteCanvasResults || this.state !== 'finished') return;

    const currentDocId = getCurrentDocId(this.host);
    const focusPlan = getCanvasFocusPlan(this.answer, currentDocId);
    if (!focusPlan) return;
    if (this._focusedCanvasSelectionKey === focusPlan.key) return;
    if (hasFocusedCanvasResult(focusPlan.key)) return;
    if (focusPlan.shouldOpenTargetDoc && focusPlan.targetDocId) {
      this.openTargetDocOnce(focusPlan.targetDocId, focusPlan.key);
      return;
    }
    if (focusPlan.targetDocId && !currentDocId) return;
    if (
      !focusCanvasIds(this.host, [
        ...focusPlan.elementIds,
        ...focusPlan.blockIds,
      ])
    ) {
      return;
    }

    this._focusedCanvasSelectionKey = focusPlan.key;
    markFocusedCanvasResult(focusPlan.key);
  }

  private affectedRefResults(obj: StreamObject) {
    if (obj.type !== 'tool-result') return [];
    const createdDoc = extractDocCreationResult(obj);
    if (createdDoc) {
      return [
        {
          title: createdDoc.title,
          content: 'Öffnen',
          onClick: (event?: Event) => {
            event?.preventDefault();
            this.dispatchEvent(
              new CustomEvent('navigate-to-source', {
                bubbles: true,
                composed: true,
                detail: {
                  kind: 'canvas-affected-ref',
                  docId: createdDoc.docId,
                  requestId: `created-doc-${obj.toolCallId}`,
                },
              })
            );
          },
        },
      ];
    }

    const refs = (obj as { affectedRefs?: AgentAffectedRef[] }).affectedRefs;
    if (!refs?.length) return [];

    const docId = refs.find(ref => ref.docId)?.docId;
    if (!docId) {
      const workflow = refs.find(ref => ref.workflowId);
      if (!workflow?.workflowId) return [];
      return [
        {
          title: 'Workflow affected',
          content: workflow.nodeId
            ? `${workflow.workflowId} / ${workflow.nodeId}`
            : workflow.workflowId,
        },
      ];
    }

    const blockIds = refs.flatMap(ref => (ref.blockId ? [ref.blockId] : []));
    const elementIds = refs.flatMap(ref =>
      ref.elementId ? [ref.elementId] : []
    );

    return [
      {
        title: 'Open affected area',
        content: [
          blockIds.length ? `${blockIds.length} block(s)` : '',
          elementIds.length ? `${elementIds.length} canvas element(s)` : '',
        ]
          .filter(Boolean)
          .join(', '),
        onClick: (event?: Event) => {
          event?.preventDefault();
          this.dispatchEvent(
            new CustomEvent('navigate-to-source', {
              bubbles: true,
              composed: true,
              detail: {
                kind: 'canvas-affected-ref',
                docId,
                blockIds,
                elementIds,
                requestId: `affected-ref-${obj.toolCallId}`,
              },
            })
          );
        },
      },
    ];
  }
}
